import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import Input from "components/shared/Input";
import Select from "components/shared/Select";

const propTypes = {
  actionOptions: PropTypes.array.isRequired,
  beForgottenOptions: PropTypes.array.isRequired,
  data: PropTypes.object,
  dataTransferOptions: PropTypes.array.isRequired,
  errorMessages: PropTypes.object,
  requestDataAccessOptions: PropTypes.array.isRequired,
  stopProcessingDataOptions: PropTypes.array.isRequired
};

const defaultProps = {
  data: {},
  errorMessages: {}
};

// Actual <form> is in the rails view
const GdprRequestForm = (props) => {
  const [action, setAction] = useState(null);

  useEffect(() => {
    setAction(props.data.action);
  }, []);

  function onActionChange(option) {
    setAction(option.value);
  }

  let ActionComponent;

  const {
    beForgottenOptions,
    dataTransferOptions,
    requestDataAccessOptions,
    stopProcessingDataOptions
  } = props;

  switch (action) {
    case "be_forgotten":
      // eslint-disable-next-line react/no-unstable-nested-components
      ActionComponent = () => (
        <>
          <Select
            errorMessage={props.errorMessages.reason}
            isRequired
            labelText="Why do you want your data to be deleted?"
            id="gdpr-request-reason"
            name="gdpr_request[reason]"
            placeholder="Select an option"
            options={beForgottenOptions}
            value={props.data.reason}
          />
          <Input
            name="gdpr_request[additional_info]"
            inputId="gdpr-request-additional-info"
            placeholder="Additional info"
            labelText="Optionally, specify which data you want to make sure LitCharts will delete"
            InputComponent="textarea"
            value={props.data.additional_info}
            errorMessage={props.errorMessages.additionalInfo}
          />
        </>
      );
      break;
    case "data_transfer":
      // eslint-disable-next-line react/no-unstable-nested-components
      ActionComponent = () => (
        <>
          <Select
            errorMessage={props.errorMessages.transferRecipient}
            isRequired
            labelText="To whom do you want LitCharts to transfer your data?"
            id="gdpr_request[transfer_recipient]"
            name="gdpr_request[transfer_recipient]"
            placeholder="Select an option"
            options={dataTransferOptions}
            value={props.data.reason}
          />
          <Input
            errorMessage={props.errorMessages.transfer3rdParty}
            name="gdpr_request[transfer_3rd_party]"
            isRequired
            placeholder="Third party"
            labelText="Specify the third party and how we should contact them"
            InputComponent="textarea"
            value={props.data.transfer_3rd_party}
          />
          <Input
            name="gdpr_request[additional_info]"
            placeholder="Additional info"
            labelText="Optionally, describe your request in more detail. Which data should be transferred?"
            InputComponent="textarea"
            value={props.data.additional_info}
            errorMessage={props.errorMessages.additionalInfo}
          />
        </>
      );
      break;
    case "data_access":
      // eslint-disable-next-line react/no-unstable-nested-components
      ActionComponent = () => (
        <>
          <Select
            errorMessage={props.errorMessages.reason}
            isRequired
            labelText="What information about your data would you like to access?"
            id="gdpr_request[reason]"
            name="gdpr_request[reason]"
            placeholder="Select an option"
            options={requestDataAccessOptions}
            value={props.data.reason}
          />
          <Input
            name="gdpr_request[additional_info]"
            placeholder="Additional info"
            labelText="Optionally, explain your question in more detail"
            InputComponent="textarea"
            value={props.data.additional_info}
            errorMessage={props.errorMessages.additionalInfo}
          />
        </>
      );
      break;
    case "rectification":
      // eslint-disable-next-line react/no-unstable-nested-components
      ActionComponent = () => (
        <>
          <Input
            errorMessage={props.errorMessages.incorrectData}
            name="gdpr_request[incorrect_data]"
            placeholder="Description"
            labelText="Describe the incorrect data that LitCharts has about you"
            InputComponent="textarea"
            value={props.data.incorrect_data}
            isRequired
          />
          <Input
            name="gdpr_request[correct_data]"
            placeholder="Description"
            labelText="Describe the correct version of the same data"
            InputComponent="textarea"
            value={props.data.correct_data}
            errorMessage={props.errorMessages.correctData}
            isRequired
          />
        </>
      );
      break;
    case "stop_marketing_profiling":
      // eslint-disable-next-line react/no-unstable-nested-components
      ActionComponent = () => (
        <Input
          name="gdpr_request[additional_info]"
          placeholder="Additional info"
          labelText="Optionally, specify more details"
          InputComponent="textarea"
          value={props.data.additional_info}
          errorMessage={props.errorMessages.additionalInfo}
        />
      );
      break;
    case "stop_processing":
      // eslint-disable-next-line react/no-unstable-nested-components
      ActionComponent = () => (
        <>
          <Select
            errorMessage={props.errorMessages.reason}
            isRequired
            labelText="Why do you want LitCharts to stop processing your data?"
            id="gdpr_request[reason]"
            name="gdpr_request[reason]"
            placeholder="Select an option"
            options={stopProcessingDataOptions}
            value={props.data.reason}
          />
          <Input
            name="gdpr_request[additional_info]"
            placeholder="Additional info"
            labelText="Optionally, please describe your request in more detail"
            InputComponent="textarea"
            value={props.data.additional_info}
            errorMessage={props.errorMessages.additionalInfo}
          />
        </>
      );
      break;
    case "objection":
      // eslint-disable-next-line react/no-unstable-nested-components
      ActionComponent = () => (
        <Input
          name="gdpr_request[additional_info]"
          placeholder="Additional info"
          labelText="Please describe the nature of your objection to how LitCharts is using your personal information"
          InputComponent="textarea"
          value={props.data.additional_info}
          errorMessage={props.errorMessages.additionalInfo}
          isRequired
        />
      );
      break;
    default:
      ActionComponent = () => null;
  }

  return (
    <>
      <Input
        errorMessage={props.errorMessages.name}
        name="gdpr_request[name]"
        inputId="gdpr-request-name"
        value={props.data.name}
        isRequired
        labelText="What's your name?"
        placeholder="Your name"
      />
      <Input
        errorMessage={props.errorMessages.email}
        name="gdpr_request[email]"
        inputId="gdpr-request-email"
        value={props.data.email}
        isRequired
        labelText="Email address (we won't share your email address, ever)"
        placeholder="Your email"
      />
      <Select
        errorMessage={props.errorMessages.action}
        id="gdpr-request-action"
        isRequired
        name="gdpr_request[action]"
        onChange={onActionChange}
        placeholder="Select an option"
        labelText="What is your specific privacy related request?"
        options={props.actionOptions}
        value={props.data.action}
      />
      <ActionComponent />
    </>
  );
};

GdprRequestForm.propTypes = propTypes;
GdprRequestForm.defaultProps = defaultProps;

export default GdprRequestForm;
